// The reveal card (sprint 27 item 3, roadmap 60): one fixed card after every doorkeeper's call, the same format every
// time, naming what the visitor was and quoting their best line from this door's own chat. Built to be reused by the
// share cards (sprint 28): cardOf() is plain data, layoutCard() and drawCard() draw it into any one-bit raster (bits.js)
// with any sentence-text layer (words.js), at any width.
// The quote only ever comes from the visitor's own lines in this door's history: never the doorkeeper's words, never a
// line from another door (a slip that is not in this door's lines is ignored). quoteBy says whether a person wrote it,
// so a share card can leave a human knocker's words out.
import { T } from './bits.js';

export const CARD_TITLE = 'Who was at the door';
export const QUOTE_MAX_LINES = 3;
export const QUOTE_WHOLE = 56; // characters: what a phone's card shows in two lines without an ellipsis
export const CARD_EVENT = 'card-seen'; // the message ui.js sends once a card has been shown (counted on /admin/measurements)

const IDENTITY = { alien: 'a visitor', infected: 'infected' };
export function identityOf(name, kind, player) {
  const who = name || 'They';
  if (kind === 'alien') return `${who} was ${IDENTITY.alien}.`;
  if (kind === 'infected') return `${who} was ${IDENTITY.infected}.`;
  return `${who} was ${player ? 'a real player' : 'a neighbour'}.`;
}
export const verdictLineOf = verdict => (verdict === 'open' ? 'You opened the door.' : verdict === 'shut' ? 'You kept the door shut.' : verdict === 'away' ? 'You sent them to the shelter.' : 'Time ran out.');

// The best line: the one that gave them away (the server's slip) if it is one of their lines here; else, when tonight's
// rule is known, the line that made the door hard: their answer to a question about the rule (the dodge), then their
// answer to any question; else the one with the most to it (long enough to read, short enough to quote whole; a
// question or an exclamation scores a little more). preferred: the lines to pick from first (dodgesOf).
export function bestLine(lines, slip = null, preferred = []) {
  const own = lines.map(l => String(l ?? '').trim()).filter(Boolean);
  if (!own.length) return null;
  if (slip?.text && own.includes(slip.text.trim())) return slip.text.trim();
  const score = s => Math.min(s.length, 80) - Math.max(0, s.length - 80) + (/[?!]/.test(s) ? 5 : 0) + (s.split(/\s+/).length >= 3 ? 10 : 0);
  const pref = preferred.map(l => String(l ?? '').trim()).filter(l => own.includes(l));
  // sprint 31: a line that fits two lines of the card is quoted whole; a longer one only when there is no shorter (the card cuts it, never a short one)
  const whole = list => list.filter(l => l.length <= QUOTE_WHOLE);
  const pick = whole(pref).length ? whole(pref) : whole(own).length ? whole(own) : pref.length ? pref : own;
  return pick.reduce((best, s) => (score(s) >= score(best) ? s : best));
}

// The words of tonight's rule that a question about it would use ("colour", "number", "seven"...): the page is never
// served the rules' own patterns (game/tells.js), so the bulletin's words stand in for them.
const RULE_STOP = new Set(('visitors visitor infected never name names like the and not any are you for who why how can with ' +
  'write say says said word words such ever always only about them they their this that from will what when your use uses').split(' '));
export const ruleWordsOf = rule => [...new Set(String(rule ?? '').toLowerCase().match(/[a-z]+/g) ?? [])]
  .filter(w => w.length >= 3 && !RULE_STOP.has(w)).map(w => w.replace(/(?<=...)s$/, ''));
// The visitor's answers in this door's chat: the first line after each doorkeeper line. Those to a question about
// tonight's rule come first; with none, the answers to any question. asks: a test for "asks about the rule" (the server's
// tell.asks, for a share card made there); otherwise the rule's words.
export function dodgesOf(history, { rule = '', asks = null } = {}) {
  const words = ruleWordsOf(rule), hits = asks ?? (q => words.some(w => q.toLowerCase().includes(w)));
  const onRule = [], answers = [];
  let q = null;
  for (const h of history) {
    if (!h) continue;
    if (h.from !== 'visitor') { q = String(h.text ?? ''); continue; }
    if (q !== null) { (hits(q) ? onRule : answers).push(h.text); q = null; }
  }
  return onRule.length ? onRule : answers;
}

// history: this door's chat, [{ from: 'visitor' | 'doorkeeper', text }]. player: a person knocked (the server's why line
// 'A real player.'); otherwise the visitor was the game's own (an alien, the infected, or a scripted neighbour).
// rule: tonight's rule as the page shows it (the dodge is picked by it); asks: see dodgesOf.
export function cardOf({ name, kind, verdict, history = [], slip = null, player = false, rule = '', asks = null }) {
  const lines = history.filter(h => h && h.from === 'visitor').map(h => h.text);
  const quote = bestLine(lines, slip, rule || asks ? dodgesOf(history, { rule, asks }) : []);
  return { title: CARD_TITLE, name: name || '', kind, identity: identityOf(name, kind, player), quote, quoteBy: player ? 'human' : 'ai',
    verdict, verdictLine: verdictLineOf(verdict) };
}
// What a screen reader hears (and what a text share would say).
export const cardText = c => [c.identity, c.quote ? `They said: “${c.quote}”` : 'They said nothing.', c.verdictLine].join(' ');

// The portrait: the glass arch with who was there (also the verdict alert's icon). 28 x 34, ink and paper; an alien's
// eyes red, the infected plague green (the art bible's two allowed reveal colours).
export function mugshot(B, ix, iy, kind) {
  const { fill, px, S } = B;
  fill(ix, iy, 28, 34, 0);
  for (let j = 0; j < 12; j++) for (let i = 0; i < 28; i++) if (Math.hypot(i - 13.5, j - 12) > 13.5) px(ix + i, iy + j, 1);
  if (kind === 'alien') { // a head stretched up on a long neck, eyes red in dark sockets
    for (let j = 0; j < 34; j++) for (let i = 0; i < 28; i++) if (Math.hypot((i - 13.5) / 5, (j - 9) / 8) < 1 || (Math.abs(i - 13.5) < 1.5 && j > 15) || Math.hypot((i - 13.5) / 11, (j - 36) / 10) < 1) px(ix + i, iy + j, 1);
    for (const [ex, ey] of [[9, 9], [15, 9], [12, 3]]) { fill(ix + ex, iy + ey - 1, 4, 3, 0); S.tint = T.red; fill(ix + ex + 1, iy + ey, 2, 1, 1); S.tint = 0; }
  } else {
    S.tint = kind === 'infected' ? T.green : 0;
    for (let j = 0; j < 34; j++) for (let i = 0; i < 28; i++) if (Math.hypot(i - 13.5, (j - 13) * 1.1) < 6 || Math.hypot((i - 13.5) / 12, (j - 34) / 12) < 1) px(ix + i, iy + j, 1);
    S.tint = 0;
    if (kind === 'infected') for (const [ex, ey] of [[11, 10], [16, 14], [9, 26], [18, 29]]) px(ix + ex, iy + ey, 0);
  }
}

// cut lines to n, the last one ending in an ellipsis when something was left out
function clip(words, lines, role, w, n) {
  if (lines.length <= n) return lines;
  const out = lines.slice(0, n);
  let last = out[n - 1];
  while (last.length > 1 && words.measure(last + '…”', role) > w) last = last.replace(/\s*\S+$/, '') || last.slice(0, -1);
  out[n - 1] = last + '…”';
  return out;
}

const PAD = 8, TITLE_H = 17, PIC = { w: 28, h: 34 };
// The fixed layout: a striped title bar, the portrait and who they were, their line in quotes, a dotted rule and the call.
// maxH: the room there is; the quote gives up lines first (down to one), then the call's line.
// verdict (sprint 27 fix pass: one card, one window): the verdict alert merged into the card, so who they were is said
// once and the portrait drawn once. { points: '-100', note: the why line, noteMax: its most lines, foot: the rows kept
// at the bottom for the buttons the page draws }. The points go under who they were (with room for a x-tag after them),
// and the call's line leads the why line in the verdict text. The quote gives up lines first, then the why line.
export function layoutCard(card, words, w, maxH = Infinity, verdict = null) {
  const idW = w - PAD * 2 - PIC.w - 8, qW = w - PAD * 2 - 10;
  const idLines = words.wrap(card.identity, 'title', idW).slice(0, 2);
  const tlh = words.line('title'), clh = words.line('chat'), plh = words.line('print');
  const said = card.quote ? `“${card.quote}”` : 'They said nothing.';
  const all = words.wrap(said, 'chat', qW);
  const ptsH = verdict ? Math.round(words.line('points')) : 0;
  const th = verdict?.compact ? 0 : TITLE_H; // compact (sprint 31, a short phone with a slipped line to show): no title bar, the portrait starts at the top
  const top = th + 6 + Math.max(PIC.h, Math.round(idLines.length * tlh) + ptsH) + 6;
  const flh = verdict ? words.line('verdict') : plh;
  const footAll = verdict ? words.wrap([verdict.noQuote === 'bare' ? '' : card.verdictLine, verdict.note].filter(Boolean).join(' '), 'verdict', w - PAD * 2).slice(0, verdict.noteMax ?? 6) : [card.verdictLine];
  const footH = m => 5 + Math.round(m * flh) + 6 + (verdict?.foot ?? 0);
  const fits = (n, m) => top + Math.round(n * clh) + footH(m) <= maxH;
  const noQuote = !!verdict?.noQuote; // the page shows the quoted line itself (ringed above the card), so the why keeps the room
  let n = noQuote ? 0 : Math.min(QUOTE_MAX_LINES, all.length), m = footAll.length;
  // sprint 31: a quote that fits its three lines is shown whole. With the verdict merged in, the why line gives up lines first (down to two), and only then the quote
  while (verdict && !noQuote && m > 2 && !fits(n, m)) m--;
  while (n > 1 && !fits(n, m)) n--;
  while (verdict && m > 1 && !fits(n, m)) m--;
  const quoteCut = !noQuote && !!card.quote && n < Math.min(QUOTE_MAX_LINES, all.length); // squeezed for room (a quote only the line cap cuts is not)
  const showFoot = !!verdict || fits(n, m);
  const quoteLines = noQuote ? [] : card.quote ? clip(words, all, 'chat', qW, n) : all.slice(0, n);
  const footLines = m < footAll.length ? clipNote(words, footAll, w - PAD * 2, m) : footAll;
  const h = top + Math.round(quoteLines.length * clh) + (showFoot ? footH(footLines.length) : 6);
  return { w, h, th, idLines, quoteLines, quoteCut, top, showFoot, tlh, clh, plh, verdict: verdict ? { ...verdict, lines: footLines, flh, ptsH, cut: footLines.length < footAll.length } : null };
}
function clipNote(words, lines, w, n) {
  const out = lines.slice(0, n);
  let last = out[n - 1];
  while (last.length > 1 && words.measure(last + '…', 'verdict') > w) last = last.replace(/\s*\S+$/, '') || last.slice(0, -1);
  out[n - 1] = last + '…';
  return out;
}

// Draw at (x, y). B: a bits.js raster; words: a words.js layer (say, cover, line, measure). ox: the raster's sideways
// shift (S.ox) the words must follow. Ink and paper only, apart from the portrait's eyes.
// Returns where the page puts the rest of a merged verdict: the x-tag after the points (tagX, tagY: its centre row)
// and the top of the button rows (buttonsY).
export function drawCard(B, words, x, y, card, lay, ox = 0) {
  const { fill, box, hline, px } = B, { w, h } = lay, V = lay.verdict;
  words.cover(x, y, w + 3, h + 3);
  fill(x + 3, y + 3, w, h, 0);
  fill(x, y, w, h, 1); box(x, y, w, h, 0); box(x + 2, y + 2, w - 4, h - 4, 0);
  // the title bar: stripes, the title on a paper gap (left out of a compact card)
  if (lay.th) {
    for (let j = y + 4; j < y + TITLE_H - 1; j += 2) hline(x + 4, j, w - 8, 0);
    hline(x + 2, y + TITLE_H, w - 4, 0);
    const tw = Math.ceil(words.measure(card.title, 'title')); fill(Math.round(x + w / 2 - tw / 2 - 6), y + 3, tw + 12, TITLE_H - 4, 1);
    words.say(card.title, x + w / 2 + ox, Math.round(y + 2 + (TITLE_H - 2 - lay.tlh) / 2), 'title', 0, 'center');
  }
  const py = y + lay.th + 6, tx = x + PAD + PIC.w + 8;
  mugshot(B, x + PAD, py, card.kind);
  const idH = Math.round(lay.idLines.length * lay.tlh) + (V ? V.ptsH : 0);
  const idTop = py + Math.max(0, Math.round((PIC.h - idH) / 2));
  lay.idLines.forEach((l, i) => words.say(l, tx + ox, Math.round(idTop + i * lay.tlh), 'title', 0));
  const out = { tagX: null, tagY: null, buttonsY: null };
  if (V) {
    const ptsY = Math.round(idTop + lay.idLines.length * lay.tlh), pw = Math.ceil(words.measure(V.points, 'points'));
    words.say(V.points, tx + ox, ptsY, 'points', 0);
    Object.assign(out, { tagX: tx + pw + 6, tagY: ptsY + V.ptsH / 2 });
  }
  // the quote: a thick ink bar down its left, like a quote in print
  const qy = y + lay.top, qh = Math.round(lay.quoteLines.length * lay.clh);
  if (qh) fill(x + PAD, qy + 1, 3, Math.max(4, qh - 2), 0);
  lay.quoteLines.forEach((l, i) => words.say(l, x + PAD + 10 + ox, Math.round(qy + i * lay.clh), 'chat', 0));
  if (lay.showFoot) {
    const fy = qy + qh + 4;
    for (let i = x + PAD; i < x + w - PAD; i += 2) px(i, fy, 0);
    if (V) {
      V.lines.forEach((l, i) => words.say(l, x + PAD + ox, Math.round(fy + 4 + i * V.flh), 'verdict', 0));
      out.buttonsY = y + h - V.foot;
    } else words.say(card.verdictLine, x + PAD + ox, fy + 4, 'print', 0);
  }
  return out;
}
